import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, Check, Sparkles, Zap, Shield, Cpu, Car, Plane, Factory, Radio, Smartphone } from 'lucide-react';
import { Container, EyebrowLabel, SectionTitle } from '../components/Section';
import HeroBanner from '../components/HeroBanner';
import { companyMetrics, services, industries, whyErbity, process, testimonials } from '../mock';
import { CaseStudyHomeStrip } from './CaseStudies';

const industryIcons = {
  semiconductor: Cpu,
  automotive: Car,
  aerospace: Plane,
  industrial: Factory,
  consumer: Smartphone,
  telecom: Radio,
};

export default function Home() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-[#FAF7F5]">
      {/* ---------- HERO ---------- */}
      <HeroBanner
        image="/hero/home.jpg"
        height="min-h-[640px] lg:min-h-[720px]"
        overlay="soft"
      >
        <div className="pt-[128px] lg:pt-[152px] pb-16 lg:pb-20">
          <Container>
            <div className="grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8 erbity-fade-up">
                <EyebrowLabel>Specialist Engineering Staffing</EyebrowLabel>
                <SectionTitle as="h1" className="mt-6 text-[44px] sm:text-[58px] lg:text-[76px] leading-[1.02]">
                  Talent that closes<br />
                  <span className="italic text-[#7E2E4A]">timing</span>. Teams that ship <span className="italic text-[#7E2E4A]">silicon</span>.
                </SectionTitle>
                <p className="mt-8 text-[17px] lg:text-[19px] text-[#3B333A] max-w-[640px] leading-relaxed erbity-fade-up-1">
                  Erbity is a specialist staffing partner for semiconductor, VLSI, embedded,
                  automotive, aerospace and industrial engineering companies
                  &mdash; delivering permanent, project and RPO talent that ships on the first spin.
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-4 erbity-fade-up-2">
                  <Link to="/contact" className="group inline-flex items-center gap-2 bg-[#1A1420] text-white text-[15px] font-medium pl-6 pr-2 py-2 rounded-full hover:bg-[#3D1A2A] transition-colors">
                    <span className="py-1">Hire specialist talent</span>
                    <span className="w-10 h-10 rounded-full bg-[#C4506F] flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                      <ArrowUpRight size={16} />
                    </span>
                  </Link>
                  <Link to="/services" className="inline-flex items-center gap-2 text-[15px] text-[#1A1420] border-b border-[#1A1420]/30 hover:border-[#7E2E4A] pb-1">
                    See how we engage <ArrowRight size={15} />
                  </Link>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/pitch-deck`}
                    className="inline-flex items-center gap-2 text-[15px] text-[#1A1420] border-b border-[#1A1420]/30 hover:border-[#7E2E4A] pb-1"
                    download
                  >
                    Download pitch deck <ArrowRight size={15} />
                  </a>
                </div>
              </div>

              <div className="lg:col-span-4 erbity-fade-up-3">
                <div className="rounded-2xl bg-white/90 backdrop-blur-xl border border-white/60 p-6 shadow-lg shadow-[#7E2E4A]/5">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A6A72] mb-4">Trusted across the stack</div>
                  <ul className="space-y-3 text-[14px] text-[#1A1420]">
                    <li className="flex items-center gap-2"><Check size={15} className="text-[#7E2E4A]" /> Fortune-500 fabless & IDMs</li>
                    <li className="flex items-center gap-2"><Check size={15} className="text-[#7E2E4A]" /> Tier-1 automotive OEMs</li>
                    <li className="flex items-center gap-2"><Check size={15} className="text-[#7E2E4A]" /> Aerospace primes & suppliers</li>
                    <li className="flex items-center gap-2"><Check size={15} className="text-[#7E2E4A]" /> Industrial & IoT scale-ups</li>
                  </ul>
                </div>
              </div>
            </div>
          </Container>
        </div>
      </HeroBanner>

      {/* ---------- METRICS ---------- */}
      <section className="relative py-16 lg:py-20 bg-[#FAF7F5]">
        <Container>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#EADDE2] border border-[#EADDE2] rounded-2xl overflow-hidden">
            {companyMetrics.primary.map((m) => (
              <div key={m.label} className="bg-[#FAF7F5] p-6 lg:p-8">
                <div className="font-serif text-[38px] lg:text-[48px] leading-none text-[#1A1420]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>{m.value}</div>
                <div className="mt-2 text-[14px] font-medium text-[#1A1420]">{m.label}</div>
                <div className="text-[12.5px] text-[#7A6A72] mt-1">{m.sublabel}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ---------- SERVICES ---------- */}
      <section className="py-16 lg:py-20 bg-white border-y border-[#EADDE2]">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <EyebrowLabel>What we do</EyebrowLabel>
              <SectionTitle className="mt-5 text-[36px] lg:text-[52px] leading-[1.05]">
                Three ways to hire, one accountable partner.
              </SectionTitle>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[16px] text-[#4E434A] leading-relaxed">
                Whether you need a single principal engineer or a full offshore engineering pod,
                Erbity matches the engagement model to your program &mdash; and stays
                on the hook until your team is delivering.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {services.map((s, i) => (
              <Link key={s.id} to={`/services#${s.id}`} className="group relative flex flex-col rounded-2xl border border-[#EADDE2] p-8 bg-[#FAF7F5] hover:bg-[#F2E3E8] transition-colors">
                <div className="flex items-start justify-between">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A2E48]">0{i + 1}</div>
                  <ArrowUpRight size={18} className="text-[#1A1420] group-hover:rotate-45 transition-transform duration-300" />
                </div>
                <h3 className="mt-6 font-serif text-[26px] leading-tight text-[#1A1420]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>{s.title}</h3>
                <p className="mt-1 text-[13px] italic text-[#7E2E4A]">{s.tagline}</p>
                <p className="mt-4 text-[14px] text-[#4E434A] leading-relaxed">{s.description}</p>
                <div className="mt-6 pt-5 border-t border-[#EADDE2] text-[12.5px] text-[#7A6A72]">{s.meta}</div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* ---------- INDUSTRIES ---------- */}
      <section className="py-14 lg:py-16 bg-[#FAF7F5]">
        <Container>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <EyebrowLabel>Industries we serve</EyebrowLabel>
              <SectionTitle className="mt-5 text-[36px] lg:text-[52px]">Six sectors, one bar for engineers.</SectionTitle>
            </div>
            <Link to="/industries" className="inline-flex items-center gap-2 text-[15px] text-[#1A1420] border-b border-[#1A1420]/30 hover:border-[#7E2E4A] pb-1 self-start lg:self-auto">
              Explore all industries <ArrowRight size={15} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {industries.map((ind) => {
              const Icon = industryIcons[ind.id] || Cpu;
              return (
                <div key={ind.id} className="group relative bg-white rounded-2xl overflow-hidden border border-[#EADDE2] hover:border-[#C4506F] transition-colors">
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={ind.image}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      style={{ filter: 'saturate(0.9) brightness(0.94)' }}
                    />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(20,16,26,0) 45%, rgba(20,16,26,0.55) 100%)' }} />
                    <div className="absolute inset-0" style={{ background: 'radial-gradient(60% 60% at 80% 90%, rgba(196,80,111,0.35), transparent 65%)' }} />
                    <div className="absolute bottom-3 left-3 w-9 h-9 rounded-lg bg-white/95 backdrop-blur flex items-center justify-center text-[#7E2E4A] shadow-sm">
                      <Icon size={18} />
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-[22px] text-[#1A1420]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>{ind.name}</h3>
                    <p className="mt-3 text-[14px] text-[#4E434A] leading-relaxed">{ind.blurb}</p>
                    <div className="mt-5 flex flex-wrap gap-1.5">
                      {ind.tags.map((t) => (
                        <span key={t} className="text-[11.5px] px-2.5 py-1 rounded-full bg-[#FAF7F5] border border-[#EADDE2] text-[#4E434A]">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ---------- CASE STUDIES STRIP ---------- */}
      <CaseStudyHomeStrip />

      {/* ---------- WHY ERBITY ---------- */}
      <section className="py-20 lg:py-24 bg-[#1A1420] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(#E7A9B9 1px, transparent 1px)', backgroundSize: '26px 26px' }} />
        <Container className="relative">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <EyebrowLabel dark>Why Erbity</EyebrowLabel>
              <SectionTitle dark className="mt-5 text-[36px] lg:text-[52px] leading-[1.05]">
                Recruiters who can read a datasheet.
              </SectionTitle>
              <p className="mt-6 text-[16px] text-white/70 leading-relaxed">
                Erbity was founded by semiconductor engineers, for semiconductor companies.
                We know the difference between an engineer who &ldquo;knows Verilog&rdquo; and
                one who can close timing on a 5nm tape-out under a deadline.
              </p>

              <div className="mt-10 grid grid-cols-3 gap-4">
                {companyMetrics.secondary.slice(0, 3).map((m) => (
                  <div key={m.label}>
                    <div className="font-serif text-[28px] lg:text-[34px] text-white" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>{m.value}</div>
                    <div className="mt-1 text-[12px] text-white/50">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {whyErbity.map((w, i) => (
                <div key={w.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:bg-white/[0.06] transition-colors">
                  <div className="flex items-center gap-3 text-[#E7A9B9]">
                    {i === 0 && <Sparkles size={16} />}
                    {i === 1 && <Zap size={16} />}
                    {i === 2 && <Shield size={16} />}
                    {i === 3 && <Cpu size={16} />}
                    {i === 4 && <Check size={16} />}
                    {i === 5 && <Shield size={16} />}
                    <span className="text-[11px] uppercase tracking-[0.22em]">0{i + 1}</span>
                  </div>
                  <h4 className="mt-4 font-serif text-[19px] text-white" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>{w.title}</h4>
                  <p className="mt-2 text-[13.5px] text-white/60 leading-relaxed">{w.body}</p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ---------- PROCESS ---------- */}
      <section className="py-14 lg:py-16 bg-[#FAF7F5]">
        <Container>
          <div className="max-w-2xl mb-14">
            <EyebrowLabel>How we deliver</EyebrowLabel>
            <SectionTitle className="mt-5 text-[36px] lg:text-[52px]">A five-step engineering-grade process.</SectionTitle>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {process.map((p) => (
              <div key={p.step} className="group relative bg-white rounded-2xl border border-[#EADDE2] overflow-hidden hover:-translate-y-1 transition-transform duration-300">
                <div className="relative h-32 overflow-hidden">
                  <img
                    src={p.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    style={{ filter: 'saturate(0.85) brightness(0.9)' }}
                  />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(20,16,26,0) 40%, rgba(20,16,26,0.6) 100%)' }} />
                  <div className="absolute inset-0" style={{ background: 'radial-gradient(70% 70% at 90% 90%, rgba(196,80,111,0.35), transparent 65%)' }} />
                  <div className="absolute top-3 left-3 font-serif text-[24px] text-white leading-none" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, textShadow: '0 1px 3px rgba(0,0,0,0.35)' }}>{p.step}</div>
                </div>
                <div className="p-5">
                  <h4 className="font-medium text-[16px] text-[#1A1420]">{p.title}</h4>
                  <p className="mt-2 text-[13px] text-[#4E434A] leading-relaxed">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ---------- TESTIMONIAL STRIP ---------- */}
      <section className="py-16 lg:py-20 bg-white border-y border-[#EADDE2]">
        <Container>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <figure key={i} className="relative bg-[#FAF7F5] rounded-2xl p-8 border border-[#EADDE2]">
                <div className="absolute -top-4 left-8 text-[64px] text-[#C4506F]/50 font-serif leading-none select-none" style={{ fontFamily: "'Fraunces', serif" }}>&ldquo;</div>
                <blockquote className="text-[15.5px] leading-relaxed text-[#1A1420] pt-2">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-[#EADDE2]">
                  <div className="text-[13.5px] font-medium text-[#1A1420]">{t.name}</div>
                  <div className="text-[12.5px] text-[#7A6A72]">{t.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </Container>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="py-14 lg:py-16 bg-[#FAF7F5]">
        <Container>
          <div className="relative overflow-hidden rounded-3xl border border-[#EADDE2] bg-gradient-to-br from-[#FDEFF3] via-[#FAF7F5] to-[#F5D9E1] p-10 lg:p-16">
            <div className="absolute -right-20 -bottom-20 w-[380px] h-[380px] rounded-full blur-3xl opacity-40" style={{ background: 'radial-gradient(closest-side, #E7A9B9, transparent 70%)' }} />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <EyebrowLabel>Let&rsquo;s build your team</EyebrowLabel>
                <SectionTitle className="mt-5 text-[34px] lg:text-[48px] leading-[1.05]">
                  Ready to shortlist engineers who can actually build silicon?
                </SectionTitle>
                <p className="mt-5 text-[16px] text-[#4E434A] max-w-xl">
                  Tell us the role, the node and the deadline. We&rsquo;ll return
                  a domain-vetted shortlist within 72 hours.
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Link to="/contact" className="group inline-flex items-center gap-2 bg-[#1A1420] text-white text-[15px] font-medium pl-6 pr-2 py-2 rounded-full hover:bg-[#3D1A2A] transition-colors">
                  <span className="py-1">Start a conversation</span>
                  <span className="w-10 h-10 rounded-full bg-[#C4506F] flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                    <ArrowUpRight size={16} />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
